import type { IPlatformDetection } from './types';

const userAgent = navigator.userAgent;

const IPHONE_X_SCREEN_HEIGHT = 2436;
const IPHONE_X_SCREEN_WIDTH = 1125;

const IPHONE_XS_MAX_SCREEN_HEIGHT = 2688;
const IPHONE_XS_MAX_SCREEN_WIDTH = 1242;

const IPHONE_XR_SCREEN_HEIGHT = 1792;
const IPHONE_XR_SCREEN_WIDTH = 828;


function detectChrome() {
    const isChrome = userAgent.includes('Chrome');
    return { isChrome };
}

function detectAndroid() {
    const androidMatch = userAgent.match(/Android\s([0-9\.]*)/);
    const isAndroid = androidMatch != null;

    let androidVersion = undefined;
    let androidDevice = undefined;

    if (isAndroid) {
        const androidVersionString = androidMatch ? androidMatch[1] : false;
        if (androidVersionString) {
            androidVersion = {
                number: parseFloat(androidVersionString),
                string: androidVersionString,
            };
        }
        if (androidMatch) {
            androidDevice = {
                isStockVersion: !detectChrome().isChrome && !userAgent.includes('UCBrowser'),
            };
        }
    }

    return { isAndroid, androidVersion, androidDevice };
}

function detectIOS() {
    const isIOS = /iPhone|iPad|iPod/g.test(userAgent);

    let iOSVersion = undefined;
    let isIPad = undefined;
    let isIPhone = undefined;
    let isIPod = undefined;
    let iPhoneXSeries = undefined;

    if (isIOS) {
        const versionMatch = userAgent.match(/OS\s+(\d{1,2}).(\d)/);
        iOSVersion = versionMatch ? versionMatch[1] + '.' + versionMatch[2] : '0';

        isIPad = userAgent.includes('iPad');
        isIPhone = userAgent.includes('iPhone');
        isIPod = userAgent.includes('iPod');

        const windowScreen = window.screen;
        const ratio = window.devicePixelRatio || 1;
        const screenWidth = windowScreen.width * ratio;
        const screenHeight = windowScreen.height * ratio;

        const minDimension = Math.min(screenHeight, screenWidth);
        const maxDimension = Math.max(screenHeight, screenWidth);

        const isIPhoneX = minDimension === IPHONE_X_SCREEN_WIDTH && maxDimension === IPHONE_X_SCREEN_HEIGHT;   // X, XS
        const isIPhoneXSMax = minDimension === IPHONE_XS_MAX_SCREEN_WIDTH && maxDimension === IPHONE_XS_MAX_SCREEN_HEIGHT; // XS Max
        const isIPhoneXR = minDimension === IPHONE_XR_SCREEN_WIDTH && maxDimension === IPHONE_XR_SCREEN_HEIGHT;    // XR

        iPhoneXSeries = isIPhoneX || isIPhoneXSMax || isIPhoneXR;
    }

    return { isIOS, iOSVersion, isIPad, isIPhone, isIPod, iPhoneXSeries };
}

function detectFirefox() {
    const isFirefox = (/Firefox\/\d+\./).test(userAgent);
    return { isFirefox };
}

function detectIE() {
    const isIe = userAgent.includes('Trident');
    return { isIe };
}

function detectOperaMini() {
    // @ts-ignore
    const isOperamini = !!window?.operamini;
    return { isOperamini };
}

function detectYandexBrowser() {
    const isYandexBrowser = userAgent.includes('YaBrowser');
    return { isYandexBrowser };
}

function detectOkApp() {
    const appUserAgent = userAgent.match(/OK(iOS|Android)\/([0-9.]+).*(\bO[k|K]App\b)/);
    let OKAppInfo = undefined;
    let isOkApp = false;

    if (appUserAgent) {
        OKAppInfo = {
            platform: appUserAgent[1],
            versionString: appUserAgent[2],
            version: appUserAgent[2].split('.').map(Number),
        };
        isOkApp = true;
    } else if (/OK(iOS|Android).*(\bO[k|K]App\b)/.test(userAgent)) {
        OKAppInfo = {};
        isOkApp = true;
    }

    return { isOkApp, OKAppInfo };
}

function detectDesktop(isAndroid: boolean, isIOS: boolean) {
    const isDesktop = !isAndroid && !isIOS && !userAgent.includes('Mobile');
    return { isDesktop };
}

export function createPlatformDetection(): IPlatformDetection {
    // Detect all platforms
    const chrome = detectChrome();
    const android = detectAndroid();
    const ios = detectIOS();
    const firefox = detectFirefox();
    const ie = detectIE();
    const operamini = detectOperaMini();
    const yandexBrowser = detectYandexBrowser();
    const okApp = detectOkApp();
    const desktop = detectDesktop(android.isAndroid, ios.isIOS);

    const platformNames = [
        chrome.isChrome && 'chrome',
        android.isAndroid && 'android',
        ios.isIOS && 'iOS',
        firefox.isFirefox && 'firefox',
        ie.isIe && 'ie',
        operamini.isOperamini && 'operamini',
        yandexBrowser.isYandexBrowser && 'yandexBrowser',
        okApp.isOkApp && 'okApp',
        desktop.isDesktop && 'desktop',
    ].filter(Boolean) as string[];

    const name = platformNames.join(',') || 'unknown';

    const result: IPlatformDetection = {
        chrome: chrome.isChrome,
        android: android.isAndroid,
        iOS: ios.isIOS,
        firefox: firefox.isFirefox,
        ie: ie.isIe,
        operamini: operamini.isOperamini,
        yandexBrowser: yandexBrowser.isYandexBrowser,
        okApp: okApp.isOkApp,
        desktop: desktop.isDesktop,
        name,
    };

    if (android.androidVersion) {
        result.androidVersion = android.androidVersion;
    }

    if (android.androidDevice) {
        result.androidDevice = android.androidDevice;
    }

    if (ios.iOSVersion) {
        result.iOSVersion = ios.iOSVersion;
    }

    if (ios.isIPad !== undefined) {
        result.isIPad = ios.isIPad;
    }

    if (ios.isIPhone !== undefined) {
        result.isIPhone = ios.isIPhone;
    }

    if (ios.isIPod !== undefined) {
        result.isIPod = ios.isIPod;
    }

    if (ios.iPhoneXSeries !== undefined) {
        result.iPhoneXSeries = ios.iPhoneXSeries;
    }

    if (okApp.OKAppInfo) {
        result.OKAppInfo = okApp.OKAppInfo;
    }

    return result;
}

let detectionInstance: IPlatformDetection | null = null;

function getPlatformDetection(): IPlatformDetection {
    if (!detectionInstance) {
        detectionInstance = createPlatformDetection();
    }
    return detectionInstance;
}

export const platformDetection = getPlatformDetection();
